'use client';

import { SignInDialog } from "@eliadmin/core/src/components/dialog/lazy-dialogs";
import { prefetchSocialAuthProviders } from '@eliadmin/core/src/components/auth/social-auth-providers-cache';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useMediaQuery } from '@eliadmin/core/src/hooks/use-media-query';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Link from '@eliadmin/core/src/components/link';
import { useState } from 'react';
import { FiLock, FiUser } from 'react-icons/fi';

type HeaderProfileButtonProps = { className?: { profile?: string }; order?: number };

export const HeaderProfileButton = ({ className, order }: HeaderProfileButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const { t } = useTranslations();

  const me = useCustomerStore(s => s.me);
  const locale = useLocaleStore(s => s.currentLocale);

  const openDialog = useDialogStore(s => s.open);

  const warmSocialAuthProviders = () => {
    void prefetchSocialAuthProviders(locale);
  };

  const handleSignInClick = () => {
    warmSocialAuthProviders();
    openDialog({ id: SignInDialog.id, content: <SignInDialog /> });
  };

  const supportsHover = useMediaQuery('(hover: hover)');

  return (
    <div className="relative" style={{ order: order }}>
      {me ? (
        <Link
          href={ROUTES.PROFILE() ?? '#'}
          className={cn('ea-header-main__profile color-primary', className?.profile)}
          prefetch={false}
        >
          <Button
            variant="default"
            size="icon-lg"
            className="bg-primary text-white hover:bg-primary/90"
          >
            {HEADER_CONFIG.headerMain.profile.icon}
          </Button>
        </Link>
      ) : (
        <Button
          variant="default"
          size="icon-lg"
          className="bg-primary text-white hover:bg-primary/90"
          onClick={() => handleSignInClick()}
          onMouseEnter={() => supportsHover && setIsHovered(true)}
          onMouseLeave={() => supportsHover && setIsHovered(false)}
        >
          {HEADER_CONFIG.headerMain.profile.icon}
        </Button>
      )}
      {isHovered && !me && (
        <div
          className="absolute right-0 top-[40px] mt-2 w-32 rounded-xl bg-white shadow-xl"
          style={{ zIndex: 1000 }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="flex flex-col p-1">
            <Button
              variant="outline"
              size="sm"
              className="mb-1 min-w-0 border-none p-0 font-semibold shadow-none outline-none"
              onClick={() => handleSignInClick()}
            >
              <FiUser className="mr-2" />
              {t('text190')}
            </Button>
            <Link
              href={ROUTES.REGISTER() ?? '#'}
              style={{ order: order }}
              className=" min-w-0 border-none p-0 font-semibold shadow-none outline-none"
              prefetch={false}
            >
              <Button
                variant="outline"
                size="sm"
                className="w-full min-w-0 border-none p-0 font-semibold shadow-none outline-none"
              >
                <FiLock className="mr-2" />
                {t('text261')}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};
